import { useEffect, useRef, useState } from "react";
import { formatDistanceToNow } from "date-fns";

import { ApiError, newULID } from "@/lib/api/client";
import { useCreateGrowReading } from "@/lib/api/grow-readings";
import { useReservoir } from "@/lib/api/reservoirs";
import type { Grow } from "@/lib/api/types";
import { useLiveStream } from "@/lib/livestream/use-livestream";
import { usePreferences } from "@/lib/preferences";
import {
  cycleLevelUnit,
  ecUnitLabel,
  effectiveLevelUnit,
  levelFromGal,
  levelToGal,
  levelUnitsAvailable,
  tempFromCelsius,
  tempUnitLabel,
  type LevelUnit,
} from "@/lib/units";
import {
  EMPTY_PENDING,
  hasAnyValue,
  pendingToCreateInput,
  snapshotFromSample,
  type PendingReading,
} from "@/lib/capture/snapshot";
import {
  useCaptureSelection,
  type CaptureHandlers,
} from "@/lib/capture/selection";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { FormField } from "@/components/forms/field";
import { LiveMetric, fmt, trimNum } from "@/components/capture/live-metric";

// ReadingCaptureCard is the shared "take a reading" surface (issue 3): a live
// pen strip, a one-tap Capture that snapshots pH/EC/temp into editable fields,
// a manual reservoir-level entry (issue 1), and inline ✓ save / ✗ discard. It
// owns its pending state and persists a grow_reading itself, so the grow detail
// Overview and every dashboard card share ONE capture behavior.
//
// Capture is button-driven here; the keyboard shortcut (`c`) and card selection
// are layered on by the CaptureSelectionProvider.
export function ReadingCaptureCard({ grow }: { grow: Grow }) {
  const { temperatureUnit, ecUnit, levelUnit, setLevelUnit } = usePreferences();
  const live = useLiveStream();
  const reservoirQ = useReservoir(grow.reservoir_id);
  const createReading = useCreateGrowReading(grow.id);

  // pending.level is canonical GALLONS (like the refill flow's ReservoirState);
  // the field is shown/edited in the user's unit via `levelText`, resynced below.
  const [pending, setPending] = useState<PendingReading>(EMPTY_PENDING);
  const [levelText, setLevelText] = useState("");
  const [levelFocused, setLevelFocused] = useState(false);
  // Transient "pressed c with no live sample" hint for the keyboard path.
  const [noPenFlash, setNoPenFlash] = useState(false);

  const sample = live.lastSample;
  const dirty = hasAnyValue(pending);

  // Level depth↔gallons needs the bound reservoir's geometry; without a bound
  // reservoir (or usable factors) the field stays gallons-only.
  const factors = {
    galPerInch: reservoirQ.data?.gal_per_inch,
    cmPerGal: reservoirQ.data?.cm_per_gal,
  };
  const availableLevelUnits = grow.reservoir_id
    ? levelUnitsAvailable(factors)
    : (["gal"] as LevelUnit[]);
  const canConvertLevel = availableLevelUnits.length > 1;
  const effLevelUnit: LevelUnit = grow.reservoir_id
    ? effectiveLevelUnit(levelUnit, factors)
    : "gal";

  // Resync the displayed level text from canonical gallons whenever the stored
  // value, the unit, or the geometry changes (and the user isn't mid-edit). This
  // is what makes a level typed before the reservoir loaded — or while another
  // card's unit toggle flips the shared preference — re-render in the right unit
  // instead of being silently reinterpreted at save.
  useEffect(() => {
    if (levelFocused) return;
    setLevelText(
      pending.level === ""
        ? ""
        : trimNum(levelFromGal(Number(pending.level), effLevelUnit, factors)),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending.level, effLevelUnit, factors.galPerInch, factors.cmPerGal, levelFocused]);

  // Clear a stale "Saved ✓" / error the moment the grower starts a new reading.
  const beginEntry = () => {
    if (createReading.isSuccess || createReading.isError) createReading.reset();
  };

  // Snapshot the live sample into the pending fields. Re-running (pressing `c`
  // again) re-stamps pH/EC/temp from the latest sample but preserves the manual
  // level + notes.
  const capture = () => {
    if (!sample) {
      setNoPenFlash(true);
      return;
    }
    setNoPenFlash(false);
    beginEntry();
    setPending((prev) =>
      snapshotFromSample(sample.sample, sample.receivedAt, { ecUnit, temperatureUnit }, prev),
    );
  };

  // Editing a pen-measured metric (pH/EC/temp) means it's no longer a pure pen
  // reading → flip to manual + drop the pen timestamp. Level/notes are always
  // manual addenda, so editing them leaves the source intact.
  const editMetric = (field: "ph" | "ec" | "temp") => (v: string) => {
    beginEntry();
    setPending((p) => ({ ...p, [field]: v, source: "manual", capturedAt: null }));
  };
  const editNotes = (v: string) => {
    beginEntry();
    setPending((p) => ({ ...p, notes: v }));
  };

  // Level field: keep the display text local; store gallons in pending.level.
  const onLevelText = (raw: string) => {
    beginEntry();
    setLevelText(raw);
    const n = parseFloat(raw);
    setPending((p) => ({
      ...p,
      level: Number.isFinite(n) ? String(levelToGal(n, effLevelUnit, factors)) : "",
    }));
  };

  const onCycleLevelUnit = () => {
    const next = cycleLevelUnit(effLevelUnit, factors);
    // pending.level (gallons) is unchanged; just re-render the field in `next`.
    setLevelText(
      pending.level === ""
        ? ""
        : trimNum(levelFromGal(Number(pending.level), next, factors)),
    );
    setLevelUnit(next);
  };

  const save = () => {
    // pending.level is already gallons, so tell the converter the level unit is
    // "gal" (identity) and only pH/EC/temp convert from their display units.
    const input = pendingToCreateInput(pending, newULID(), {
      ecUnit,
      temperatureUnit,
      levelUnit: "gal",
      factors: {},
    });
    createReading.mutate(input, {
      onSuccess: () => {
        setPending(EMPTY_PENDING);
        setNoPenFlash(false);
      },
    });
  };

  const discard = () => {
    beginEntry();
    setPending(EMPTY_PENDING);
    setNoPenFlash(false);
  };

  // Selection + keyboard wiring (issue 3). When inside a CaptureSelectionProvider
  // the card is selectable (click / focus) and `c` snapshots into it. Register a
  // STABLE ref whose .current always holds the latest capture/discard closures so
  // the provider's one keydown listener stays current without re-registering.
  const sel = useCaptureSelection();
  const handlersRef = useRef<CaptureHandlers>({ capture, discard });
  handlersRef.current = { capture, discard };
  // Depend on the STABLE register/selectIfUnset (useCallback([])), not the `sel`
  // object — `sel` is recreated on every selection change, which would otherwise
  // re-run this effect for every card.
  const register = sel?.register;
  const selectIfUnset = sel?.selectIfUnset;
  useEffect(() => {
    if (!register || !selectIfUnset) return;
    const unregister = register(grow.id, handlersRef);
    // Arm this card on mount ONLY if no card is selected yet, so expanding a
    // second card doesn't steal the `c` hotkey from one the user already armed.
    selectIfUnset(grow.id);
    return unregister;
  }, [register, selectIfUnset, grow.id]);
  const selected = !!sel && sel.selectedGrowId === grow.id;
  const selectionProps = sel
    ? {
        tabIndex: 0,
        role: "group" as const,
        "aria-label": `Take reading for ${grow.species}`,
        onClick: () => sel.select(grow.id),
        onFocus: () => sel.select(grow.id),
      }
    : {};

  const liveSample = sample?.sample;
  const livePh = liveSample ? (liveSample.ph_filtered ?? liveSample.ph) : undefined;
  const penHint = sample
    ? `pen ${formatDistanceToNow(sample.receivedAt, { addSuffix: true })}`
    : "no live pen sample";

  // Inline save feedback (no global toast system).
  const saveStatus = createReading.isError ? (
    <span className="text-xs text-destructive">
      {createReading.error instanceof ApiError
        ? createReading.error.message
        : "Save failed."}
    </span>
  ) : createReading.isSuccess && !dirty ? (
    <span className="text-xs text-emerald-600 dark:text-emerald-400">Saved ✓</span>
  ) : noPenFlash ? (
    <span className="text-xs text-muted-foreground">No live pen sample to capture.</span>
  ) : null;

  // Announce the pending capture / result for keyboard + screen-reader users.
  const announcement = noPenFlash
    ? "No live pen sample to capture."
    : dirty
      ? `Captured reading: pH ${pending.ph || "none"}, EC ${pending.ec || "none"}, temperature ${pending.temp || "none"}. Save or discard below.`
      : createReading.isSuccess
        ? "Reading saved."
        : "";

  return (
    <Card
      {...selectionProps}
      className={cn(
        "transition-shadow focus:outline-none",
        selected && "ring-2 ring-ring ring-offset-2",
      )}
    >
      <CardHeader>
        <CardTitle className="text-sm font-medium text-muted-foreground">
          Take reading
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <span className="sr-only" aria-live="polite">
          {announcement}
        </span>
        <div className="flex items-start justify-between gap-4">
          <dl className="flex flex-wrap gap-x-6 gap-y-1">
            <LiveMetric label="pH" value={fmt(livePh, 2)} />
            <LiveMetric label={ecUnitLabel(ecUnit)} value={fmt(liveSample?.ec_ms_per_cm, 2)} />
            <LiveMetric
              label={`temp ${tempUnitLabel(temperatureUnit)}`}
              value={
                typeof liveSample?.temperature_c === "number"
                  ? tempFromCelsius(liveSample.temperature_c, temperatureUnit).toFixed(1)
                  : "—"
              }
            />
          </dl>
          <div className="flex flex-col items-end gap-1">
            <Button type="button" size="sm" disabled={!sample} onClick={capture}>
              Capture from pen
              {sel && (
                <kbd className="ml-1.5 rounded border bg-muted px-1 text-[10px] font-medium">
                  c
                </kbd>
              )}
            </Button>
            <span className="text-right text-[10px] text-muted-foreground">{penHint}</span>
          </div>
        </div>

        <div className={"grid gap-3 " + (grow.reservoir_id ? "grid-cols-4" : "grid-cols-3")}>
          <FormField label="pH">
            <Input type="number" step="any" value={pending.ph} onChange={(e) => editMetric("ph")(e.target.value)} />
          </FormField>
          <FormField label={`EC (${ecUnit === "ppm700" ? "ppm" : "mS/cm"})`}>
            <Input type="number" step="any" value={pending.ec} onChange={(e) => editMetric("ec")(e.target.value)} />
          </FormField>
          <FormField label={`temp ${tempUnitLabel(temperatureUnit)}`}>
            <Input type="number" step="any" value={pending.temp} onChange={(e) => editMetric("temp")(e.target.value)} />
          </FormField>
          {grow.reservoir_id && (
            <div className="space-y-1">
              <div className="flex items-center gap-1">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">level</Label>
                {canConvertLevel ? (
                  <button
                    type="button"
                    onClick={onCycleLevelUnit}
                    title="Tap to switch gal / in / cm"
                    className="text-xs uppercase tracking-wide text-muted-foreground underline decoration-dotted underline-offset-2 hover:text-foreground"
                  >
                    ({effLevelUnit} ▾)
                  </button>
                ) : (
                  <span className="text-xs uppercase tracking-wide text-muted-foreground">
                    ({effLevelUnit})
                  </span>
                )}
              </div>
              <Input
                type="number"
                step="any"
                value={levelText}
                onFocus={() => setLevelFocused(true)}
                onBlur={() => setLevelFocused(false)}
                onChange={(e) => onLevelText(e.target.value)}
              />
            </div>
          )}
        </div>

        <FormField label="Notes (optional)">
          <Input
            value={pending.notes}
            onChange={(e) => editNotes(e.target.value)}
            placeholder="e.g. lower leaves yellowing"
          />
        </FormField>

        <div className="flex flex-wrap items-center gap-2">
          {dirty ? (
            <>
              <Button
                type="button"
                size="sm"
                disabled={createReading.isPending}
                onClick={save}
                title="Save reading"
              >
                {createReading.isPending ? "Saving…" : "✓ Save"}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={createReading.isPending}
                onClick={discard}
                title="Discard reading"
              >
                ✗ Discard
              </Button>
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                {pending.source === "pen" ? "pen" : "manual"}
              </span>
            </>
          ) : (
            <span className="text-xs text-muted-foreground">
              Capture from the pen or type a reading, then save.
            </span>
          )}
          {saveStatus}
        </div>
      </CardContent>
    </Card>
  );
}
